// Auth Store

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import type { IUser } from '@/shared/types';
import { authApi } from '@/shared/api';
import { i18n } from '@/shared/i18n';

/**
 * Отказ аутентификации — единственная причина, по которой чтение профиля
 * прекращает сессию (login-sessions-R09). Прочие исходы, включая сбой
 * продукта и обрыв связи, сессию не трогают.
 */
function isAuthenticationRefusal(error: unknown): boolean {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return false;
  }
  const response = (error as { response?: unknown }).response;
  if (response === null || typeof response !== 'object' || !('status' in response)) {
    return false;
  }
  return (response as { status?: unknown }).status === 401;
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<IUser | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => user.value !== null);
  const currentUser = computed(() => user.value);
  const currentUserLogin = computed(() => user.value?.login ?? '');
  const mustChangePassword = computed(() => user.value?.mustChangePassword ?? false);
  const isAdmin = computed(() => user.value?.isAdmin ?? false);

  // Actions
  async function login(identifier: string, password: string): Promise<{ user: IUser; accessToken: string; refreshToken: string }> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.login({ identifier, password });
      
      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      
      // Store user
      user.value = response.user;
      
      return response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err && typeof err.response === 'object' && err.response !== null && 'data' in err.response && typeof err.response.data === 'object' && err.response.data !== null && 'message' in err.response.data && typeof err.response.data.message === 'string' ? err.response.data.message : i18n.global.t('auth.loginError');
      error.value = errorMessage;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function changePassword(oldPassword: string, newPassword: string): Promise<{ user: IUser; accessToken: string; refreshToken: string }> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.changePassword({ oldPassword, newPassword });
      
      // Update tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      
      // Update user
      user.value = response.user;
      
      return response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err && typeof err.response === 'object' && err.response !== null && 'data' in err.response && typeof err.response.data === 'object' && err.response.data !== null && 'message' in err.response.data && typeof err.response.data.message === 'string' ? err.response.data.message : i18n.global.t('auth.changePasswordError');
      error.value = errorMessage;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    
    try {
      if (refreshToken !== null && refreshToken.length > 0) {
        await authApi.logout(refreshToken);
      }
    } catch (err) {
      if (import.meta.env.MODE !== 'test') {
        console.error('Logout error:', err);
      }
    } finally {
      // Clear local state
      user.value = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  async function logoutAll(): Promise<void> {
    try {
      await authApi.logoutAll();
    } catch (err) {
      if (import.meta.env.MODE !== 'test') {
        console.error('Logout all error:', err);
      }
    } finally {
      // Clear local state
      user.value = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Перечитать профиль.
   *
   * Выход выполняется **только при отказе аутентификации**
   * (access-and-auth-login-sessions-R09, основание: вердикт 246): единичный
   * сбой чтения профиля не повод выводить человека с живой сессией на вход —
   * ему показывается сообщение, и он остаётся в продукте.
   *
   * Отказа «требуется смена пароля» здесь не бывает вовсе: профиль приходит
   * вместе с признаком (sessions-R17), и по признаку пользователя ведёт на
   * смену пароля охрана переходов (R03, R12).
   */
  async function refreshUser(): Promise<void> {
    if (isAuthenticated.value === false) {
      return;
    }

    try {
      user.value = await authApi.getCurrentUser();
    } catch (err) {
      if (import.meta.env.MODE !== 'test') {
        console.error('Refresh user error:', err);
      }
      if (isAuthenticationRefusal(err)) {
        await logout();
        return;
      }
      ElMessage.error(i18n.global.t('auth.profileLoadFailed'));
    }
  }

  async function initializeAuth(): Promise<boolean> {
    const token = localStorage.getItem('accessToken');
    if (token === null || token.length === 0) {
      return false;
    }

    try {
      user.value = await authApi.getCurrentUser();
      return true;
    } catch (err) {
      if (import.meta.env.MODE !== 'test') {
        console.error('Initialize auth error:', err);
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return false;
    }
  }

  return {
    // State
    user,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    currentUser,
    currentUserLogin,
    mustChangePassword,
    isAdmin,
    
    // Actions
    login,
    changePassword,
    logout,
    logoutAll,
    refreshUser,
    initializeAuth,
  };
});

